// NEW_FILE_JUSTIFIED: New card component for displaying per-platform style analysis results
import { useState, useCallback, useMemo } from 'react';
import { FileText, RefreshCw, Pencil, X, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import type { IAiStyleProfile, TAiReplyPlatform, ILearnedRule, TRuleType } from '@/@types/aiReply';
import { LearnedRulesList } from '@/components/aiReply/LearnedRulesList';
import type { Id } from '@convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: need Id type for rule callbacks

interface IStyleAnalysisResultsCardProps {
  // LOCAL_TYPE_JUSTIFIED: props interface for this component
  profiles: IAiStyleProfile[];
  analyzeEnabled: {
    gmail: boolean;
    whatsapp: boolean;
    telegram: boolean;
  };
  onTriggerAnalysis: (platform: TAiReplyPlatform, lookbackDays?: number) => Promise<void>;
  onUpdateProfile: (platform: TAiReplyPlatform, editedProfile: string) => Promise<void>;
  lookbackDays: number;
  learnedRules: ILearnedRule[];
  onAddRule: (rule: string, ruleType: TRuleType, platform?: TAiReplyPlatform) => Promise<void>;
  onUpdateRule: (
    ruleId: Id<'aiLearnedRules'>,
    updates: { rule?: string; ruleType?: TRuleType }
  ) => Promise<void>;
  onToggleRule: (ruleId: Id<'aiLearnedRules'>, isActive: boolean) => Promise<void>;
  onDeleteRule: (ruleId: Id<'aiLearnedRules'>) => Promise<void>;
}

const PLATFORM_LABELS: Record<TAiReplyPlatform, string> = {
  gmail: 'Gmail',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
};

const PLATFORMS: TAiReplyPlatform[] = ['gmail', 'whatsapp', 'telegram'];

export function StyleAnalysisResultsCard({
  profiles,
  analyzeEnabled,
  onTriggerAnalysis,
  onUpdateProfile,
  lookbackDays,
  learnedRules,
  onAddRule,
  onUpdateRule,
  onToggleRule,
  onDeleteRule,
}: IStyleAnalysisResultsCardProps) {
  const [activeTab, setActiveTab] = useState<string>('gmail');

  const globalRules = useMemo(
    () => learnedRules.filter(r => r.platform === undefined),
    [learnedRules]
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText aria-hidden="true" className="h-4 w-4" />
          Style Analysis Results
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Global rules section */}
        <div className="mb-4">
          <p className="mb-2 text-sm font-medium text-foreground">Global Rules</p>
          <p className="mb-2 text-xs text-muted-foreground">
            These rules apply to AI replies on all platforms.
          </p>
          <LearnedRulesList
            rules={globalRules}
            onAddRule={onAddRule}
            onUpdateRule={onUpdateRule}
            onToggleRule={onToggleRule}
            onDeleteRule={onDeleteRule}
          />
        </div>

        <div className="mb-4 border-t" />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            {PLATFORMS.map(platform => (
              <TabsTrigger key={platform} value={platform}>
                {PLATFORM_LABELS[platform]}
              </TabsTrigger>
            ))}
          </TabsList>

          {PLATFORMS.map(platform => {
            const profile = profiles.find(p => p.platform === platform);
            const isEnabled = analyzeEnabled[platform];

            return (
              <TabsContent key={platform} value={platform}>
                <PlatformAnalysisTab
                  platform={platform}
                  profile={profile ?? null}
                  isEnabled={isEnabled}
                  onTriggerAnalysis={onTriggerAnalysis}
                  onUpdateProfile={onUpdateProfile}
                  lookbackDays={lookbackDays}
                  learnedRules={learnedRules}
                  onAddRule={onAddRule}
                  onUpdateRule={onUpdateRule}
                  onToggleRule={onToggleRule}
                  onDeleteRule={onDeleteRule}
                />
              </TabsContent>
            );
          })}
        </Tabs>
      </CardContent>
    </Card>
  );
}

interface IPlatformAnalysisTabProps {
  // LOCAL_TYPE_JUSTIFIED: internal sub-component props
  platform: TAiReplyPlatform;
  profile: IAiStyleProfile | null;
  isEnabled: boolean;
  onTriggerAnalysis: (platform: TAiReplyPlatform, lookbackDays?: number) => Promise<void>;
  onUpdateProfile: (platform: TAiReplyPlatform, editedProfile: string) => Promise<void>;
  lookbackDays: number;
  learnedRules: ILearnedRule[];
  onAddRule: (rule: string, ruleType: TRuleType, platform?: TAiReplyPlatform) => Promise<void>;
  onUpdateRule: (
    ruleId: Id<'aiLearnedRules'>,
    updates: { rule?: string; ruleType?: TRuleType }
  ) => Promise<void>;
  onToggleRule: (ruleId: Id<'aiLearnedRules'>, isActive: boolean) => Promise<void>;
  onDeleteRule: (ruleId: Id<'aiLearnedRules'>) => Promise<void>;
}

/**
 * Render profile text with basic formatting: **bold** labels become
 * semibold spans, and line breaks are preserved via whitespace-pre-wrap.
 */
function FormattedProfile({ text }: { text: string }) {
  // LOCAL_TYPE_JUSTIFIED: inline prop type for simple render helper
  const lines = text.split('\n');

  return (
    <div className="space-y-2">
      {lines.map((line, i) => {
        if (line.trim() === '') return <div key={i} className="h-1" />;

        // Replace **text** with bold spans
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (
          <p key={i} className="text-sm leading-relaxed">
            {parts.map((part, j) => {
              if (part.startsWith('**') && part.endsWith('**')) {
                return (
                  <span key={j} className="font-semibold text-foreground">
                    {part.slice(2, -2)}
                  </span>
                );
              }
              return <span key={j}>{part}</span>;
            })}
          </p>
        );
      })}
    </div>
  );
}

function PlatformAnalysisTab({
  platform,
  profile,
  isEnabled,
  onTriggerAnalysis,
  onUpdateProfile,
  lookbackDays,
  learnedRules,
  onAddRule,
  onUpdateRule,
  onToggleRule,
  onDeleteRule,
}: IPlatformAnalysisTabProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  // LOCAL_TYPE_JUSTIFIED: tracks whether analysis is in progress for loading UI
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const platformRules = useMemo(
    () => learnedRules.filter(r => r.platform === platform),
    [learnedRules, platform]
  );

  const handleStartEdit = useCallback(() => {
    if (profile === null) return;
    const text =
      profile.isEdited === true && profile.editedProfile !== undefined
        ? profile.editedProfile
        : profile.profile;
    setEditText(text);
    setIsEditing(true);
  }, [profile]);

  const handleSaveEdit = useCallback(async () => {
    await onUpdateProfile(platform, editText);
    setIsEditing(false);
  }, [platform, editText, onUpdateProfile]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditText('');
  }, []);

  const handleTriggerAnalysis = useCallback(async () => {
    setIsAnalyzing(true);
    await onTriggerAnalysis(platform, lookbackDays);
  }, [platform, lookbackDays, onTriggerAnalysis]);

  const handleReanalyze = useCallback(async () => {
    if (profile?.isEdited === true) {
      const confirmed = window.confirm('This will overwrite your manual edits. Continue?');
      if (!confirmed) return;
    }
    setIsAnalyzing(true);
    await onTriggerAnalysis(platform, lookbackDays);
  }, [platform, lookbackDays, profile, onTriggerAnalysis]);

  // Reset analyzing state when profile arrives/updates
  if (isAnalyzing && profile !== null) {
    setIsAnalyzing(false);
  }

  if (!isEnabled) {
    return (
      <div className="rounded-md border border-dashed p-6 text-center">
        <p className="text-sm text-muted-foreground">
          Analysis is disabled for {PLATFORM_LABELS[platform]}. Enable it in the settings above.
        </p>
      </div>
    );
  }

  if (isAnalyzing) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3 rounded-md bg-muted p-4">
          <Loader2 aria-hidden="true" className="h-5 w-5 animate-spin text-muted-foreground" />
          <div>
            <p className="text-sm font-medium">Analyzing {PLATFORM_LABELS[platform]} messages...</p>
            <p className="text-xs text-muted-foreground">
              This may take a minute. The results will appear automatically.
            </p>
          </div>
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    );
  }

  if (profile === null) {
    return (
      <div className="space-y-3">
        <div className="rounded-md border border-dashed p-6 text-center">
          <p className="text-sm text-muted-foreground">
            No analysis yet for {PLATFORM_LABELS[platform]}.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void handleTriggerAnalysis()}>
          <RefreshCw aria-hidden="true" className="mr-2 h-3.5 w-3.5" />
          Run Analysis
        </Button>
      </div>
    );
  }

  const displayProfile =
    profile.isEdited === true && profile.editedProfile !== undefined
      ? profile.editedProfile
      : profile.profile;

  const analyzedDate = new Date(profile.analyzedAt).toLocaleDateString();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Analyzed on {analyzedDate} ({profile.messageCount} messages)
        </p>
        <div className="flex items-center gap-2">
          {profile.isEdited === true && (
            <Badge variant="secondary" className="text-xs">
              Manually edited
            </Badge>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-2">
          <Textarea
            value={editText}
            onChange={e => setEditText(e.target.value)}
            rows={10}
            className="text-sm"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void handleSaveEdit()}>
              <Check aria-hidden="true" className="mr-1 h-3.5 w-3.5" />
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={handleCancelEdit}>
              <X aria-hidden="true" className="mr-1 h-3.5 w-3.5" />
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="rounded-md bg-muted p-4">
          <FormattedProfile text={displayProfile} />
        </div>
      )}

      {profile.examples.length > 0 && !isEditing && (
        <div className="rounded-md border border-border p-3">
          <p className="text-xs font-semibold text-foreground mb-2">Example phrases</p>
          <ul className="space-y-1.5">
            {profile.examples.map((example, i) => (
              <li
                key={i}
                className="text-xs text-muted-foreground italic pl-3 border-l-2 border-border"
              >
                &ldquo;{example}&rdquo;
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isEditing && (
        <div className="mt-3 border-t pt-3">
          <p className="mb-2 text-sm font-medium text-foreground">
            {PLATFORM_LABELS[platform]} Rules
          </p>
          <LearnedRulesList
            rules={platformRules}
            platform={platform}
            onAddRule={onAddRule}
            onUpdateRule={onUpdateRule}
            onToggleRule={onToggleRule}
            onDeleteRule={onDeleteRule}
          />
        </div>
      )}

      <div className="flex gap-2">
        {!isEditing && (
          <>
            <Button variant="outline" size="sm" onClick={handleStartEdit}>
              <Pencil aria-hidden="true" className="mr-1 h-3.5 w-3.5" />
              Edit
            </Button>
            <Button variant="outline" size="sm" onClick={() => void handleReanalyze()}>
              <RefreshCw aria-hidden="true" className="mr-1 h-3.5 w-3.5" />
              Re-analyze
            </Button>
          </>
        )}
      </div>

      {profile.messageCount < 5 && (
        <div className="flex items-center gap-2 rounded-md bg-yellow-50 dark:bg-yellow-900/20 p-3">
          <AlertCircle
            aria-hidden="true"
            className="h-4 w-4 text-yellow-600 dark:text-yellow-400"
          />
          <p className="text-xs text-yellow-700 dark:text-yellow-300">
            Low message count ({profile.messageCount} of 5 minimum). Analysis quality may be
            limited.
          </p>
        </div>
      )}
    </div>
  );
}
